import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { adminError, isSessionRequired } from "../lib/jamoraAdmin";
import { useAdminSession } from "../components/jamora-admin/SessionProvider";

// One read of the Jamora administration API (`/api/jamora-admin/...`). `phase`: loading | ready | error. An expired session sends the
// person back to the confirmation step instead of showing an error; a newer request always wins over an older one.
export default function useAdminFetch(path, params, { enabled = true } = {}) {
  const adminSession = useAdminSession();
  const [state, setState] = useState({ phase: enabled ? "loading" : "idle", data: null, error: "" });
  const seq = useRef(0);
  const key = JSON.stringify(params || {});

  const load = useCallback(async () => {
    if (!enabled || !path) return;
    const mine = ++seq.current;
    setState((s) => ({ phase: s.phase === "ready" ? "ready" : "loading", data: s.data, error: "" }));
    try {
      const { data } = await api.get(`/jamora-admin${path}`, { params: params ? JSON.parse(key) : undefined });
      if (mine !== seq.current) return;
      setState({ phase: "ready", data, error: "" });
      adminSession?.touch();
    } catch (e) {
      if (mine !== seq.current) return;
      if (isSessionRequired(e)) { adminSession?.expire(); return; }
      setState({ phase: "error", data: null, error: adminError(e) });
    }
  }, [enabled, path, key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  return { ...state, reload: load };
}
